import { fabric } from 'fabric';
import Vec2 from 'vec2';

import { CANVAS_OBJECT_TYPE_SUFFIX, SHAPE_ROTATION_DEGREES } from '@common/constants';
import { containsBumpOut } from '@common/enums';
import { DIRECTION } from '@common/enums/drawing-types.enum';
import { MathFunctions } from '@common/helpers';
import { ArrowInfo, DrawingEdge, DrawingShape } from '@common/models';

import drawingTool from '../../../drawer/drawingTool';
import { findArrowByEdgeId, findDrawingEdgeById } from './drawing-shape-helper';
import { calculateEdgeConstraints } from './length-constraints';
import { isCorrectDirection } from './shape-helper';

export const adjustBumpOutSides = (change: number, edge: DrawingEdge): void => {
    edge.bumpOut!.counterClockwiseRemainderLength += change / 2;
    edge.bumpOut!.clockwiseRemainderLength += change / 2;
};

export const adjustEdgeForDiagonals = (shape: DrawingShape, edge: DrawingEdge): number => {
    if (!shape) {
        return 0;
    }
    const startPt = shape.cornerMap[edge.startCornerId];
    const endPt = shape.cornerMap[edge.endCornerId];
    return [startPt, endPt].reduce((acc, point) => {
        return !!point.diagonalLength && point.isVisibleInCanvas
            ? acc + Math.sqrt(Math.pow(point.diagonalLength, 2) / 2)
            : acc;
    }, 0);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const updateShapeByEdge = (arrowInfo: ArrowInfo, shape: DrawingShape, whatIf: boolean): void => {
    if (whatIf) {
        // skip the whatIf attempts for limit setting, kills performance
        return;
    }

    if (arrowInfo.isInterior) {
        const skData = shape.skeleton.slice();
        if (!arrowInfo.interiorSkIndex || !arrowInfo.size) {
            return;
        }
        skData[arrowInfo.interiorSkIndex].depth = arrowInfo.size / 2;
        drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
        if (!whatIf) {
            calculateEdgeConstraints(shape);
        }
        return;
    }
    const edge = findDrawingEdgeById(arrowInfo.drawingEdgeId!, shape);

    if (!edge) {
        return;
    }

    const arrow = findArrowByEdgeId(edge.id, shape);

    if (!arrow) {
        return;
    }

    if (arrowInfo.size === arrow.size) {
        return;
    }

    const diff = arrowInfo.size! - arrow.size!;

    if (containsBumpOut(edge)) {
        adjustBumpOutSides(diff, edge);
    }

    let skData = shape.skeleton.slice();
    const skLength = skData.length;

    const startSkId = shape.cornerMap[edge.startCornerId].skIndex;
    const endSkId = shape.cornerMap[edge.endCornerId].skIndex;
    const vec2StartSkPt = new Vec2(skData[startSkId].pos.x, skData[startSkId].pos.y);
    const vec2EndSkPt = new Vec2(skData[endSkId].pos.x, skData[endSkId].pos.y);
    let dirVec = edge.dirVec.normalize(true);
    let newStartSkPt = new Vec2(vec2StartSkPt.x, vec2StartSkPt.y);
    let newEndSkPt = new Vec2(vec2EndSkPt.x, vec2EndSkPt.y);

    if (skLength === 2 && startSkId === endSkId) {
        // This is for simple shape - Rectangle AND it's a change to an end edge
        // Got rid of half of the code that handles just a rectangle, it was not recreating vectors correctly
        // for side edges (non end edges)
        if (!isCorrectDirection(dirVec, DIRECTION.DOWN)) {
            dirVec.multiply(-1);
        }
        newStartSkPt = new Vec2(skData[0].pos.x, skData[0].pos.y).add(dirVec.multiply(diff * 0.5, true), true);
        newEndSkPt = new Vec2(skData[1].pos.x, skData[1].pos.y).add(dirVec.multiply(diff * 0.5, true), true);
        const depth = (arrowInfo.size! + adjustEdgeForDiagonals(shape, edge)) * 0.5;

        skData = [
            {
                pos: newStartSkPt,
                depth,
            },
            {
                pos: newEndSkPt,
                depth,
            },
        ];
    } else if (startSkId === endSkId) {
        //means the edge is one of two end edges
        //TODO This will also be the case for interior dimensions - does it still work?
        const depth = (arrowInfo.size! + adjustEdgeForDiagonals(shape, edge)) * 0.5;

        // get the next skeletal point OR if we're on the end of the skeleton, work backward
        let midSkIndex: number;
        let nextSkIndex: number;
        if (startSkId === skLength - 1) {
            midSkIndex = skLength - 2;
            nextSkIndex = midSkIndex - 1;
        } else {
            midSkIndex = startSkId + 1;
            nextSkIndex = midSkIndex + 1;
        }
        const midSkPt = new Vec2(skData[midSkIndex].pos.x, skData[midSkIndex].pos.y);

        // get one further skeletal point from the edge
        const nextSkPt = new Vec2(skData[nextSkIndex].pos.x, skData[nextSkIndex].pos.y);

        // determine if the first turn on the shape bends to the left or the right from the perspective of the edge looking back at the shape
        const skTurn =
            (midSkPt.x - vec2StartSkPt.x) * (nextSkPt.y - vec2StartSkPt.y) -
                (midSkPt.y - vec2StartSkPt.y) * (nextSkPt.x - vec2StartSkPt.x) >
            0
                ? 1 // "left"
                : -1; // "right"

        // get the new starting point (on this edge) considering the edge's direction, change in magnitude/2, and whether the shape bends left or right heading into the shape
        newStartSkPt = vec2StartSkPt.subtract(dirVec.multiply(diff * 0.5 * skTurn, true), true);

        const dirVecFirst = midSkPt.subtract(vec2StartSkPt, true).normalize();
        const dirVecSecond = nextSkPt.subtract(midSkPt, true).normalize();
        const intersectionPt = MathFunctions.getInterceptPoint(newStartSkPt, dirVecFirst, nextSkPt, dirVecSecond, true);
        if (intersectionPt) {
            newEndSkPt = intersectionPt;
            skData.splice(startSkId, 1, {
                pos: newStartSkPt,
                depth,
            });

            const nextIndex = startSkId === skLength - 1 ? startSkId - 1 : 1;
            skData.splice(nextIndex, 1, {
                pos: newEndSkPt,
                depth: startSkId === skLength - 1 ? depth : skData[nextIndex].depth,
            });
        } else {
            console.error('Error occured during getting new skeleton point');
        }
    } else if (startSkId === 0 || startSkId === skLength - 1) {
        let nextSkPt: Vec2;
        if (startSkId === 0) {
            nextSkPt = new Vec2(skData[1].pos.x, skData[1].pos.y);
        } else {
            nextSkPt = new Vec2(skData[skLength - 2].pos.x, skData[skLength - 2].pos.y);
        }

        dirVec = newStartSkPt.subtract(nextSkPt, true).normalize();
        newStartSkPt = newStartSkPt.add(dirVec.multiply(diff, true), true);

        skData.splice(startSkId, 1, {
            pos: newStartSkPt,
            depth: skData[startSkId].depth,
        });
    } else if (endSkId === 0 || endSkId === skLength - 1) {
        let nextSkPt: Vec2;
        if (endSkId === 0) {
            nextSkPt = new Vec2(skData[1].pos.x, skData[1].pos.y);
        } else {
            nextSkPt = new Vec2(skData[skLength - 2].pos.x, skData[skLength - 2].pos.y);
        }
        dirVec = newEndSkPt.subtract(nextSkPt, true).normalize();
        newEndSkPt = newEndSkPt.add(dirVec.multiply(diff, true), true);
        skData.splice(endSkId, 1, {
            pos: newEndSkPt,
            depth: skData[endSkId].depth,
        });
    } else {
        //In this case, we move all following or previous sk points towards the diff direction
        dirVec = vec2EndSkPt.subtract(vec2StartSkPt, true).normalize();

        if (isCorrectDirection(dirVec, DIRECTION.DOWN) || isCorrectDirection(dirVec, DIRECTION.RIGHT)) {
            const step = startSkId < endSkId ? 1 : -1;
            let i = endSkId;
            while (i >= 0 && i < skLength) {
                const newPt = new Vec2(skData[i].pos.x, skData[i].pos.y).add(dirVec.multiply(diff, true));
                skData.splice(i, 1, {
                    pos: newPt,
                    depth: skData[i].depth,
                });
                i += step;
            }
        } else {
            const step = startSkId < endSkId ? -1 : 1;
            let i = startSkId;
            while (i >= 0 && i < skLength) {
                const newPt = new Vec2(skData[i].pos.x, skData[i].pos.y).add(dirVec.multiply(diff * -1, true));
                skData.splice(i, 1, {
                    pos: newPt,
                    depth: skData[i].depth,
                });
                i += step;
            }
        }
    }

    drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
    if (whatIf) {
        return;
    }

    calculateEdgeConstraints(shape);
};

export const updateShapeByTranslation = (moveVec: Vec2, shape: DrawingShape): void => {
    const skData = shape.skeleton.map((skSeg) => {
        return {
            pos: MathFunctions.subtractVectors(skSeg.pos, moveVec),
            depth: skSeg.depth,
        };
    });

    // move all the shape's appliances as well
    shape.appliances.forEach((appliance) => {
        appliance.centerPoint = MathFunctions.subtractVectors(appliance.centerPoint, moveVec);
    });

    drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
};

export const rotatePointAboutCenterPoint = (rotatingPoint: Vec2, centerPoint: Vec2, rotInc: number): Vec2 => {
    const cos = Math.cos((rotInc * Math.PI) / 180);
    const sin = Math.sin((rotInc * Math.PI) / 180);
    const cpx = centerPoint.x;
    const cpy = centerPoint.y;
    const x = rotatingPoint.x;
    const y = rotatingPoint.y;
    return new Vec2(cos * (x - cpx) + (y - cpy) * sin + cpx, -sin * (x - cpx) + cos * (y - cpy) + cpy);
};

export const updateShapeByRotation = (
    canvas: fabric.Canvas | undefined,
    shape: DrawingShape,
    clockwise: boolean
): void => {
    // called when a user rotates a shape
    let rotInc = SHAPE_ROTATION_DEGREES;
    if (clockwise) {
        rotInc *= -1;
    }

    let centerVec: Vec2;
    if (canvas) {
        // find the canvas shape related to the current DrawingShape
        const target = canvas
            .getObjects()
            .filter((obj) => obj.name === shape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP);

        // find center point to rotate the shape about
        const centerPoint = target[0].getCenterPoint();
        centerVec = new Vec2(centerPoint.x, centerPoint.y);
    } else {
        centerVec = new Vec2(0, 0);
    }

    // get all skeleton points
    const skData = shape.skeleton.slice();
    const skLength = skData.length;

    // rotate each point in the skeleton
    let i = 0;
    while (i >= 0 && i < skLength) {
        skData[i].pos = rotatePointAboutCenterPoint(skData[i].pos, centerVec, rotInc);
        i += 1;
    }

    // revolve all the shape's appliances about the shape's center
    shape.appliances.forEach((appliance) => {
        appliance.centerPoint = rotatePointAboutCenterPoint(appliance.centerPoint, centerVec, rotInc);
        // also rotate the sink image itself
        appliance.angle -= rotInc;
    });

    drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
};
